import React, { useState, useEffect, useCallback } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { getUserByToken, login, loginWithTwoFactor } from '../core/_requests'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  KeyRound,
  ArrowLeft,
  Smartphone,
  ShieldAlert,
  Zap,
  BarChart3,
  Shield,
  CheckCircle2,
} from 'lucide-react'

// Schema Validation for Login
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Format email tidak valid')
    .min(3, 'Minimal 3 karakter')
    .max(50, 'Maksimal 50 karakter')
    .required('Email wajib diisi'),
  password: Yup.string()
    .min(3, 'Minimal 3 karakter')
    .max(50, 'Maksimal 50 karakter')
    .required('Password wajib diisi'),
})

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isErrorShake, setIsErrorShake] = useState(false)
  const [isAppOpening, setIsAppOpening] = useState(false)

  const { saveAuth, setCurrentUser, setMenus, setPermissions } = useAuth()

  // 2FA Challenge States
  const [isTwoFactorStep, setIsTwoFactorStep] = useState(false)
  const [twoFactorTempToken, setTwoFactorTempToken] = useState('')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [isBackupMode, setIsBackupMode] = useState(false)
  const [verifiedEmail, setVerifiedEmail] = useState('')

  const handleSuccessfulAuth = async (authData: any) => {
    saveAuth(authData)
    const { data: user } = await getUserByToken(authData.api_token)
    if (!user) {
      throw new Error('Akun Anda tidak valid.')
    }
    const tenantDisplay = 'Smart Billing'
    document.title = `SmartCall Apps | ${tenantDisplay}`
    if (authData?.session_id) {
      localStorage.setItem('current_session_id', authData.session_id)
    }

    // Trigger sliding "Open Application" animation!
    setIsAppOpening(true)
    await new Promise((resolve) => setTimeout(resolve, 850))

    saveAuth(authData)
    setCurrentUser(user)
    setMenus(user.menuTree)
    setPermissions(user.permissions)
  }

  // Login Formik Handler
  const loginFormik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setIsErrorShake(false)
      setStatus(undefined)
      try {
        const response = await login(values.email, values.password)
        const resData = response.data?.data || response.data

        if (resData?.require_2fa) {
          setTwoFactorTempToken(resData.temp_token)
          setVerifiedEmail(values.email)
          setIsTwoFactorStep(true)
          setTwoFactorCode('')
          setTwoFactorError(null)
          setLoading(false)
          setSubmitting(false)
          return
        }

        await handleSuccessfulAuth(resData)
      } catch (error: any) {
        console.error(error)
        localStorage.removeItem('ccs_active_tenant_key')
        localStorage.removeItem('ccs_active_tenant_name')
        setPermissions(undefined)
        setMenus(undefined)
        saveAuth(undefined)
        const errMsg =
          error?.response?.data?.message ||
          error?.message ||
          'Email atau password yang Anda masukkan salah.'
        setStatus(errMsg)
        setIsErrorShake(true)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  // 2FA Verification Submit
  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cleanCode = twoFactorCode.trim()
    if (!cleanCode) {
      setTwoFactorError(
        isBackupMode ? 'Masukkan kode cadangan pemulihan' : 'Masukkan 6-digit kode verifikasi'
      )
      setIsErrorShake(true)
      return
    }

    setLoading(true)
    setTwoFactorError(null)
    setIsErrorShake(false)

    try {
      const response = await loginWithTwoFactor(twoFactorTempToken, cleanCode)
      const resData = response.data?.data || response.data
      await handleSuccessfulAuth(resData)
    } catch (error: any) {
      console.error('2FA Login Error:', error)
      const errMsg =
        error?.response?.data?.message ||
        'Kode verifikasi 2FA tidak valid atau sudah kadaluarsa'
      setTwoFactorError(errMsg)
      setIsErrorShake(true)
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setIsTwoFactorStep(false)
    setTwoFactorTempToken('')
    setTwoFactorCode('')
    setTwoFactorError(null)
    setIsBackupMode(false)
    setLoading(false)
  }

  const handleClearErrors = useCallback(() => {
    if (isErrorShake || loginFormik.status || twoFactorError) {
      setIsErrorShake(false)
      loginFormik.setStatus(undefined)
      setTwoFactorError(null)
    }
  }, [isErrorShake, loginFormik, twoFactorError])

  useEffect(() => {
    if (loginFormik.status || twoFactorError) {
      setIsErrorShake(true)
    }
  }, [loginFormik.status, twoFactorError])

  return (
    <div className={clsx('anime-outer-bezel', isAppOpening && 'is-app-opening')}>
      <div
        className={clsx(
          'anime-auth-card',
          isAppOpening && 'is-app-opening',
          isErrorShake && 'is-shake'
        )}
      >
        {/* =======================================================
            LEFT PANEL: LOGIN CREDENTIALS / 2FA FORM
            ======================================================= */}
        <div className='anime-form-side'>
          <img
            alt='Smart Billing Logo'
            src={toAbsoluteUrl('/media/logos/logo.png')}
            className='anime-brand-logo theme-light-show'
          />
          <img
            alt='Smart Billing Logo'
            src={toAbsoluteUrl('/media/logos/logo_dark.png')}
            className='anime-brand-logo theme-dark-show'
          />

          {isTwoFactorStep ? (
            /* ── 2FA VERIFICATION STEP ── */
            <div className='anime-2fa-container'>
              <div className='text-center mb-3'>
                <div className='anime-2fa-badge'>
                  <ShieldCheck size={36} />
                </div>
                <h3 className='anime-form-title fs-3 mb-1'>Verifikasi 2FA</h3>
                <p className='anime-form-subtitle mb-3'>
                  {isBackupMode ? (
                    <>Masukkan <strong>Kode Cadangan Pemulihan</strong> akun Anda.</>
                  ) : (
                    <>
                      Masukkan 6-digit kode Authenticator untuk{' '}
                      <strong>{verifiedEmail}</strong>
                    </>
                  )}
                </p>
              </div>

              {twoFactorError && (
                <div className='anime-alert anime-alert-danger'>
                  <ShieldAlert size={16} />
                  <span>{twoFactorError}</span>
                </div>
              )}

              <form onSubmit={handleTwoFactorSubmit}>
                <div className='anime-input-wrap'>
                  <input
                    type='text'
                    autoFocus
                    placeholder={isBackupMode ? 'Contoh: A1B2-C3D4' : '000 000'}
                    maxLength={isBackupMode ? 12 : 8}
                    value={twoFactorCode}
                    onChange={(e) => {
                      handleClearErrors()
                      setTwoFactorCode(e.target.value)
                    }}
                    className='anime-input-field text-center font-monospace fs-4'
                    autoComplete='one-time-code'
                  />
                  {isBackupMode ? (
                    <KeyRound className='anime-input-icon' size={18} />
                  ) : (
                    <Smartphone className='anime-input-icon' size={18} />
                  )}
                </div>

                <button
                  type='submit'
                  className='anime-btn-primary'
                  disabled={loading || !twoFactorCode.trim()}
                >
                  {loading ? 'Memverifikasi...' : 'Verifikasi & Masuk'}
                </button>

                <div className='d-flex justify-content-between align-items-center mt-3 pt-2 border-top'>
                  <button
                    type='button'
                    className='btn btn-link text-muted fs-8 p-0 d-flex align-items-center gap-1'
                    onClick={handleBackToLogin}
                  >
                    <ArrowLeft size={14} />
                    <span>Kembali</span>
                  </button>

                  <button
                    type='button'
                    className='btn btn-link text-primary fs-8 p-0 fw-semibold'
                    onClick={() => {
                      setIsBackupMode(!isBackupMode)
                      setTwoFactorCode('')
                      setTwoFactorError(null)
                    }}
                  >
                    {isBackupMode ? 'Pakai Kode App' : 'Pakai Kode Cadangan'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* ── CREDENTIALS LOGIN FORM ── */
            <form onSubmit={loginFormik.handleSubmit} className='w-100' noValidate>
              <h2 className='anime-form-title'>Login</h2>
              <p className='anime-form-subtitle'>Welcome to Smart Billing Platform</p>

              {loginFormik.status && (
                <div className='anime-alert anime-alert-danger'>
                  <ShieldAlert size={16} />
                  <span>{loginFormik.status}</span>
                </div>
              )}

              {/* Email Input */}
              <div className='anime-input-wrap'>
                <input
                  type='email'
                  placeholder='Email'
                  autoComplete='email'
                  {...loginFormik.getFieldProps('email')}
                  onFocus={handleClearErrors}
                  className={clsx(
                    'anime-input-field',
                    loginFormik.touched.email && loginFormik.errors.email && 'is-invalid'
                  )}
                />
                <Mail className='anime-input-icon' size={18} />
              </div>

              {/* Password Input */}
              <div className='anime-input-wrap'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Password'
                  autoComplete='current-password'
                  {...loginFormik.getFieldProps('password')}
                  onFocus={handleClearErrors}
                  className={clsx(
                    'anime-input-field',
                    loginFormik.touched.password && loginFormik.errors.password && 'is-invalid'
                  )}
                />
                <button
                  type='button'
                  className='anime-pass-toggle-btn'
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Submit Button */}
              <button
                type='submit'
                className='anime-btn-primary'
                disabled={loginFormik.isSubmitting || !loginFormik.isValid || loading}
              >
                {loading ? 'Authenticating...' : 'Login'}
              </button>

              <div className='anime-security-footer'>
                <Shield size={14} />
                <span>Enterprise 256-bit Encrypted Session</span>
              </div>
            </form>
          )}
        </div>

        {/* =======================================================
            RIGHT PANEL: SLIDING CURVED OVERLAY ("OPEN APPLICATION")
            ======================================================= */}
        <div className='anime-overlay-slider'>
          {/* Normal Branding Panel */}
          <div className='anime-overlay-content anime-panel-branding'>
            <div className='anime-overlay-badge'>Enterprise Suite</div>
            <h2 className='anime-overlay-title'>Smart Billing</h2>
            <p className='anime-overlay-desc'>
              Automated Telephony &amp; Real-Time CDR Billing Management System
            </p>

            <div className='anime-feature-list'>
              <div className='anime-feature-item'>
                <Zap size={16} />
                <span>Real-Time CDR Rating Engine</span>
              </div>
              <div className='anime-feature-item'>
                <BarChart3 size={16} />
                <span>Dynamic Rate &amp; Versioning Control</span>
              </div>
              <div className='anime-feature-item'>
                <ShieldCheck size={16} />
                <span>Multi-Factor Authentication (2FA)</span>
              </div>
            </div>

            <div className='anime-status-pill'>
              <div className='anime-status-dot' />
              <span>System Operational</span>
            </div>
          </div>

          {/* Opening Application Transition Panel */}
          <div className='anime-overlay-content anime-panel-opening'>
            <div className='anime-opening-icon-wrap'>
              <CheckCircle2 size={46} />
            </div>
            <h2 className='anime-overlay-title fs-1 mb-2'>Access Granted!</h2>
            <p className='anime-overlay-desc fs-6 mb-3'>
              Login berhasil. Membuka Smart Billing Dashboard...
            </p>

            <div className='anime-loading-bar-wrap'>
              <div className='anime-loading-bar-fill' />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}